import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import CrudPage from '../../../components/common/CrudPage';
import { albumService, contentCategoryService, uploadImageToServer } from '../../../services/cms/contentManagementService';

const { Text } = Typography;

const ensureCdnUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  const baseDomain = 'https://cdn.lichviet.org';
  const path = url.startsWith('/') ? url : `/${url}`;
  return baseDomain + path;
};

const STATUS_OPTIONS = [
  { label: 'Duyệt', value: 1 },
  { label: 'Đợi duyệt', value: 2 },
  { label: 'Lưu lại', value: 3 },
  { label: 'Xóa', value: 0 },
  { label: 'Trả lại', value: 4 },
  { label: 'Đã duyệt', value: 6 },
];

const IMAGE_MODE_OPTIONS = [
  { label: 'Ảnh ngang', value: 1 },
  { label: 'Ảnh dọc', value: 2 },
];

const STATUS_CONTENT_OPTIONS = [
  { label: 'Duyệt', value: 1 },
  { label: 'Đợi duyệt', value: 2 },
];

const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  {
    title: 'TÊN ALBUM',
    dataIndex: 'title',
    key: 'title',
    render: (t) => <Text strong className="text-blue-600 hover:text-blue-800">{t}</Text>,
  },
  {
    title: 'NGƯỜI TẠO',
    dataIndex: 'author_name',
    key: 'author_name',
    width: 140,
    render: (val, r) => val || r.create_by || '-',
  },
  {
    title: 'ẢNH ĐẠI DIỆN',
    dataIndex: 'image_url',
    key: 'image_url',
    width: 100,
    render: (u) => u ? <img src={ensureCdnUrl(u)} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} /> : '-',
  },
  {
    title: 'DANH MỤC',
    dataIndex: 'category_name',
    key: 'category_name',
    width: 150,
    render: (val, r) => val ? `[${r.category_id}] ${val}` : (r.category_id || '-'),
  },
  {
    title: 'SỐ ẢNH',
    dataIndex: 'image_num',
    key: 'image_num',
    width: 80,
    align: 'center',
  },
  {
    title: 'TRẠNG THÁI',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    render: (s) => {
      switch (s) {
        case 1:
          return <Tag color="green">DUYỆT</Tag>;
        case 2:
          return <Tag color="blue">ĐỢI DUYỆT</Tag>;
        case 3:
          return <Tag color="orange">LƯU LẠI</Tag>;
        case 0:
          return <Tag color="red">XÓA</Tag>;
        case 4:
          return <Tag color="volcano">TRẢ LẠI</Tag>;
        case 6:
          return <Tag color="cyan">ĐÃ DUYỆT</Tag>;
        default:
          return <Tag color="default">CHƯA RÕ ({s})</Tag>;
      }
    },
  },
  {
    title: 'NGÀY PHÁT HÀNH',
    dataIndex: 'publish_date',
    key: 'publish_date',
    width: 140,
    render: (t) => t ? dayjs(t).format('DD/MM/YYYY') : '-',
  },
  {
    title: 'NGÀY TẠO',
    dataIndex: 'create_time',
    key: 'create_time',
    width: 140,
    render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-',
  },
];

const QuanLyAlbums = () => {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    contentCategoryService.listActive().then((res) => {
      if (Array.isArray(res)) {
        setCategories(res.map((c) => ({ label: `[${c.id}] ${c.title}`, value: c.id })));
      }
    });
  }, []);

  const filterFields = useMemo(() => [
    {
      label: 'Chọn danh mục',
      value: 'category_id',
      options: [{ label: 'Tất cả danh mục', value: '' }, ...categories],
    },
    {
      label: 'Trạng thái',
      value: 'status',
      options: [
        { label: 'Tất cả trạng thái', value: '' },
        { label: 'Duyệt', value: 1 },
        { label: 'Đợi duyệt', value: 2 },
      ],
    },
  ], [categories]);

  const formFields = useMemo(() => [
    { type: 'divider', label: 'Thông tin cơ bản' },
    { name: 'title', label: 'Tên album', type: 'input', rules: [{ required: true, message: 'Vui lòng nhập tên!' }] },
    { name: 'category_id', label: 'Danh mục', type: 'select', options: categories, propsInput: { showSearch: true } },
    { name: 'image_url', label: 'Ảnh bìa', type: 'upload-cdn' },
    { name: 'description', label: 'Mô tả', type: 'textarea' },
    { name: 'publish_date', label: 'Ngày phát hành', type: 'date', format: 'YYYY-MM-DD HH:mm:ss', submitFormat: 'YYYY-MM-DD HH:mm:ss' },
    { name: 'metadata', label: 'Metadata / SEO', type: 'textarea' },
    { name: 'status', label: 'Trạng thái', type: 'select', options: STATUS_OPTIONS, defaultValue: 1 },

    { type: 'divider', label: 'Nội dung Album (Danh sách ảnh)' },
    {
      name: 'content',
      type: 'group',
      isSort: true,
      titleButtonAdd: 'Thêm ảnh mới',
      filedsList: [
        {
          name: 'image_url',
          label: 'Ảnh',
          type: 'upload-cdn',
          span: 24,
          md: 5,
          rules: [{ required: true, message: 'Chọn ảnh!' }],
        },
        {
          name: 'description',
          label: 'Mô tả',
          type: 'textarea',
          span: 24,
          md: 7,
          rows: 2,
          autoSize: { minRows: 2, maxRows: 4 },
        },
        {
          name: 'order',
          label: 'Thứ tự',
          type: 'number',
          span: 24,
          md: 4,
          defaultValue: 1,
        },
        {
          name: 'image_mode',
          label: 'Loại ảnh',
          type: 'select',
          options: IMAGE_MODE_OPTIONS,
          span: 24,
          md: 4,
          defaultValue: 1,
        },
        {
          name: 'status',
          label: 'Trạng thái',
          type: 'select',
          options: STATUS_CONTENT_OPTIONS,
          span: 24,
          md: 4,
          defaultValue: 1,
        },
      ],
    },
  ], [categories]);

  const mapFormToData = async (payload) => {
    const content = [];
    if (Array.isArray(payload.content)) {
      for (const item of payload.content) {
        let imgUrl = null;
        if (Array.isArray(item.image_url) && item.image_url.length > 0) {
          const file = item.image_url[0];
          if (file?.originFileObj) {
            try {
              imgUrl = await uploadImageToServer(file.originFileObj);
            } catch (err) {
              console.error('Failed to upload nested image:', err);
              throw new Error(`Lỗi tải ảnh trong phần nội dung: ${err.message}`);
            }
          } else if (file?.url) {
            imgUrl = file.url;
          }
        } else if (typeof item.image_url === 'string') {
          imgUrl = item.image_url;
        }

        content.push({
          image_url: imgUrl,
          description: item.description || '',
          order: item.order !== undefined ? Number(item.order) : 0,
          image_mode: item.image_mode !== undefined ? Number(item.image_mode) : 1,
          status: item.status !== undefined ? Number(item.status) : 1,
        });
      }
    }

    const image_num = content.length;

    return {
      content: JSON.stringify(content),
      image_num,
    };
  };

  const mapDataToForm = (record) => {
    let parsedContent = [];
    if (record.content) {
      try {
        const parsed = typeof record.content === 'string' ? JSON.parse(record.content) : record.content;
        if (Array.isArray(parsed)) {
          parsedContent = parsed.map((item) => {
            let fileList = [];
            if (item.image_url) {
              const fullUrl = ensureCdnUrl(item.image_url);
              fileList = [
                {
                  uid: String(Math.random()),
                  name: item.image_url.split('/').pop() || 'image.png',
                  status: 'done',
                  url: fullUrl,
                  thumbUrl: fullUrl,
                },
              ];
            }
            return {
              ...item,
              image_url: fileList,
            };
          });
        }
      } catch (e) {
        console.error('Failed to parse album content:', e);
      }
    }

    return {
      content: parsedContent,
    };
  };

  return (
    <CrudPage
      title="QUẢN LÝ ALBUMS"
      subTitle="Quản lý album ảnh"
      itemsName="Album"
      queryKey="albumList"
      service={albumService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm..."
      addButtonLabel="Thêm album"
      mapFormToData={mapFormToData}
      mapDataToForm={mapDataToForm}
      formConfig={{ width: 1000 }}
    />
  );
};

export default QuanLyAlbums;
